import { useEffect, useState, useRef, createContext, useContext } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, CircularProgress } from "@mui/material";
import { useSalesAuth } from "../../hooks/useSalesAuth";
import {
  salesVerifySession,
  salesLogoutUser,
} from "../../store/actions/salesActions";
import axios from "axios";

// Routes that don't require authentication
const EXCLUDED_ROUTES = ["/sales/register", "/sales/login"];

// Context to share willExpireSoon state with SalesLayout
const SalesSessionContext = createContext({
  willExpireSoon: false,
  setWillExpireSoon: () => {},
});

export const useSalesSession = () => useContext(SalesSessionContext);

/**
 * SalesAuthWrapper - A wrapper component that handles sales user authentication
 *
 * Usage:
 * <SalesAuthWrapper>
 *   <YourSalesComponent />
 * </SalesAuthWrapper>
 *
 * This wrapper will:
 * 1. Check if user is authenticated in Redux
 * 2. If not authenticated, check sessionStorage for temp token
 * 3. If temp token exists, verify with backend
 * 4. If verification fails, redirect to /sales/login
 */
const SalesAuthWrapper = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, loading } = useSalesAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [willExpireSoon, setWillExpireSoon] = useState(false);
  const hasVerifiedRef = useRef(false);
  const lastAuthStateRef = useRef(isAuthenticated);
  const sessionCheckIntervalRef = useRef(null);

  // Check if current route is excluded from authentication
  const isExcludedRoute = EXCLUDED_ROUTES.includes(location.pathname);

  useEffect(() => {
    // Skip authentication for excluded routes
    if (isExcludedRoute) {
      hasVerifiedRef.current = false;
      return;
    }

    // If already authenticated, reset ref for next time
    if (isAuthenticated) {
      hasVerifiedRef.current = false;
      lastAuthStateRef.current = isAuthenticated;
      return;
    }

    // If authentication state changed from true to false, reset ref to allow re-verification
    if (lastAuthStateRef.current === true && isAuthenticated === false) {
      hasVerifiedRef.current = false;
    }
    lastAuthStateRef.current = isAuthenticated;

    // Skip if we've already verified in this authentication state
    if (hasVerifiedRef.current) {
      return;
    }

    const verifyAuth = async () => {
      // Mark as verified to prevent duplicate calls
      hasVerifiedRef.current = true;

      // Check sessionStorage for temp token
      const storedUser = sessionStorage.getItem("salesUser");
      if (!storedUser) {
        // No session found, redirect to login
        navigate("/sales/login");
        return;
      }

      try {
        const { temp_token } = JSON.parse(storedUser);
        if (!temp_token) {
          // No temp token found, redirect to login
          sessionStorage.removeItem("salesUser");
          navigate("/sales/login");
          return;
        }

        // Set verifying state to show loading
        setIsVerifying(true);

        // Verify session with backend
        const result = await dispatch(salesVerifySession());

        if (!result || !result.success) {
          // Verification failed, reset ref to allow retry if needed
          hasVerifiedRef.current = false;
          navigate("/sales/login");
        }
      } catch (error) {
        console.error("Error verifying sales session:", error);
        // On error, reset ref to allow retry if needed
        hasVerifiedRef.current = false;
        navigate("/sales/login");
      } finally {
        setIsVerifying(false);
      }
    };

    verifyAuth();
  }, [isAuthenticated, dispatch, navigate, isExcludedRoute, location.pathname]);

  // Session expiration check - poll every 1 minute
  useEffect(() => {
    if (isExcludedRoute || !isAuthenticated) {
      // Clear interval if on excluded route or not authenticated
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
      setWillExpireSoon(false);
      return;
    }

    const checkSessionExpiration = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/nocApi/sales/check-session-expiry`,
          { withCredentials: true }
        );

        if (res.data.success) {
          const { expiresIn } = res.data.data;
          // expiresIn is in seconds, check if less than 5 minutes (300 seconds)
          if (expiresIn > 0 && expiresIn < 300) {
            setWillExpireSoon(true);
          } else {
            setWillExpireSoon(false);
          }
        }
      } catch (error) {
        // If 401, session is already expired - logout and redirect
        if (error.response?.status === 401) {
          setWillExpireSoon(false);
          // Clear sessionStorage
          sessionStorage.removeItem("salesUser");
          // Logout user (this will also clear the cookie via backend)
          await dispatch(salesLogoutUser());
          // Navigate to login
          navigate("/sales/login");
        }
      }
    };

    // Check immediately
    checkSessionExpiration();

    // Then check every 1 minute (60000 ms)
    sessionCheckIntervalRef.current = setInterval(
      checkSessionExpiration,
      60000
    );

    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, isExcludedRoute, dispatch, navigate]);

  // For excluded routes, render children/Outlet without authentication check
  if (isExcludedRoute) {
    return <>{children || <Outlet />}</>;
  }

  // Show loading while verifying or if Redux is loading
  if (loading || isVerifying) {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated after verification, don't render children
  // (redirect will happen in useEffect)
  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // User is authenticated, render children or Outlet for nested routes
  // If children exist, render them (for direct wrapping), otherwise render Outlet (for nested routes)
  return (
    <SalesSessionContext.Provider value={{ willExpireSoon, setWillExpireSoon }}>
      {children || <Outlet />}
    </SalesSessionContext.Provider>
  );
};

export default SalesAuthWrapper;
